/**
 * Smart Logger Utility
 *
 * Centralized logging service that handles complex objects, errors,
 * and prevents [object Object] in console output.
 *
 * Features:
 * - Smart object serialization with circular reference detection
 * - Error stack trace formatting
 * - Recursive object traversal with depth limits
 * - Compact yet readable output
 * - Support for multiple log levels
 * - Contextual prefixes
 * - Optional file transport (flag-driven)
 */

import type { FileTransport } from './file-transport';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

type SerializationOptions = {
  maxDepth: number;
  maxArrayLength: number;
  maxStringLength: number;
  includeStackTrace: boolean;
};

const DEFAULT_OPTIONS: SerializationOptions = {
  maxDepth: 5,
  maxArrayLength: 100,
  maxStringLength: 1000,
  includeStackTrace: true,
};

/**
 * Safely serializes any value to a readable string
 */
function smartSerialize(
  value: unknown,
  options: Partial<SerializationOptions> = {},
  depth = 0,
  seen = new WeakSet()
): string {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Primitives
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') {
    const truncated =
      value.length > opts.maxStringLength
        ? `${value.slice(0, opts.maxStringLength)}... (${value.length} chars)`
        : value;
    return `"${truncated}"`;
  }
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return String(value);
  if (typeof value === 'symbol') return value.toString();
  if (typeof value === 'bigint') return `${value}n`;
  if (typeof value === 'function') return `[Function: ${value.name || 'anonymous'}]`;

  // Check depth limit
  if (depth >= opts.maxDepth) {
    return '[Max depth reached]';
  }

  // Handle errors specially
  if (value instanceof Error) {
    return serializeError(value, opts);
  }

  // Circular reference detection
  if (typeof value === 'object' && seen.has(value as object)) {
    return '[Circular Reference]';
  }

  // Add to seen set
  if (typeof value === 'object') {
    seen.add(value as object);
  }

  // Arrays
  if (Array.isArray(value)) {
    const items = value
      .slice(0, opts.maxArrayLength)
      .map(item => smartSerialize(item, opts, depth + 1, seen));
    const remaining = value.length - opts.maxArrayLength;
    const suffix = remaining > 0 ? `, ... ${remaining} more` : '';
    return `[${items.join(', ')}${suffix}]`;
  }

  // Dates
  if (value instanceof Date) {
    return value.toISOString();
  }

  // RegExp
  if (value instanceof RegExp) {
    return value.toString();
  }

  // Maps
  if (value instanceof Map) {
    const entries = Array.from(value.entries())
      .slice(0, opts.maxArrayLength)
      .map(
        ([k, v]) =>
          `${smartSerialize(k, opts, depth + 1, seen)} => ${smartSerialize(v, opts, depth + 1, seen)}`
      )
      .join(', ');
    return `Map(${value.size}) { ${entries} }`;
  }

  // Sets
  if (value instanceof Set) {
    const items = Array.from(value)
      .slice(0, opts.maxArrayLength)
      .map(item => smartSerialize(item, opts, depth + 1, seen))
      .join(', ');
    return `Set(${value.size}) { ${items} }`;
  }

  // Plain objects
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj);

    if (keys.length === 0) {
      return '{}';
    }

    const entries = keys
      .slice(0, 20) // Limit to 20 keys for readability
      .map(key => {
        const val = obj[key];
        return `${key}: ${smartSerialize(val, opts, depth + 1, seen)}`;
      })
      .join(', ');

    const remaining = keys.length - 20;
    const suffix = remaining > 0 ? `, ... ${remaining} more keys` : '';

    return `{ ${entries}${suffix} }`;
  }

  // Fallback
  return String(value);
}

/**
 * Serializes Error objects with stack traces
 */
function serializeError(error: Error, opts: SerializationOptions): string {
  const parts: string[] = [];

  parts.push(`${error.name}: ${error.message}`);

  // Add any custom properties
  const errorObj = error as unknown as Record<string, unknown>;
  const standardKeys = ['name', 'message', 'stack'];
  const customKeys = Object.keys(errorObj).filter(key => !standardKeys.includes(key));

  if (customKeys.length > 0) {
    const props = customKeys
      .map(key => `${key}: ${smartSerialize(errorObj[key], { ...opts, maxDepth: 2 })}`)
      .join(', ');
    parts.push(`(${props})`);
  }

  // Add stack trace if available and enabled
  if (opts.includeStackTrace && error.stack) {
    const stackLines = error.stack.split('\n').slice(1, 6); // First 5 stack frames
    if (stackLines.length > 0) {
      parts.push('\nStack trace:');
      parts.push(stackLines.map(line => ` ${line.trim()}`).join('\n'));
    }
  }

  return parts.join(' ');
}

/**
 * Formats arguments for logging
 */
function formatArgs(args: unknown[]): string {
  return args.map(arg => smartSerialize(arg)).join(' ');
}

/**
 * Core logging function
 */
function logInternal(level: LogLevel, prefix: string, args: unknown[], preFormatted = false): void {
  const now = new Date();
  const timestamp = now.toISOString().split('T')[1]?.slice(0, 12) || ''; // HH:MM:SS.mmm
  const fullTimestamp = now.toISOString(); // For file logging
  const levelEmoji: Record<LogLevel, string> = {
    debug: '🔍',
    info: 'ℹ️',
    warn: '⚠️',
    error: '❌',
  };

  const formattedMessage = preFormatted ? args.join(' ') : formatArgs(args);
  const consoleMessage = prefix
    ? `[${timestamp}] ${levelEmoji[level]} [${prefix}] ${formattedMessage}`
    : `[${timestamp}] ${levelEmoji[level]} ${formattedMessage}`;

  // Add the console message to the beginning of the args array to save sentry logs
  const consoleArgs = [consoleMessage, ...args];
  // Console output
  switch (level) {
    case 'debug':
      console.debug(...consoleArgs);
      break;
    case 'info':
      console.info(...consoleArgs);
      break;
    case 'warn':
      console.warn(...consoleArgs);
      break;
    case 'error':
      console.error(...consoleArgs);
      break;
  }

  // File output (if enabled)
  const fileTransport = Logger.getFileTransport();
  if (fileTransport) {
    const fileMessage = prefix
      ? `[${fullTimestamp}] [${level.toUpperCase()}] [${prefix}] ${formattedMessage}`
      : `[${fullTimestamp}] [${level.toUpperCase()}] ${formattedMessage}`;

    fileTransport.write(fileMessage);
  }
}

/**
 * Logger class with contextual prefix support
 */
export class Logger {
  private prefix: string;
  private static fileTransport: FileTransport | null = null;

  constructor(prefix = '') {
    this.prefix = prefix;
  }

  /**
   * Set global file transport for all loggers
   */
  static setFileTransport(transport: FileTransport | null): void {
    Logger.fileTransport = transport;
  }

  /**
   * Get current file transport
   */
  static getFileTransport(): FileTransport | null {
    return Logger.fileTransport;
  }

  /**
   * Create a child logger with an additional prefix
   */
  child(childPrefix: string): Logger {
    const fullPrefix = this.prefix ? `${this.prefix}:${childPrefix}` : childPrefix;
    return new Logger(fullPrefix);
  }

  /**
   * Debug level logging
   */
  debug(...args: unknown[]): void {
    logInternal('debug', this.prefix, args);
  }

  /**
   * Info level logging
   */
  info(...args: unknown[]): void {
    logInternal('info', this.prefix, args);
  }

  /**
   * Warning level logging
   */
  warn(...args: unknown[]): void {
    logInternal('warn', this.prefix, args);
  }

  /**
   * Error level logging
   */
  error(...args: unknown[]): void {
    logInternal('error', this.prefix, args);
  }

  /**
   * Log with custom serialization options
   */
  logWith(level: LogLevel, options: Partial<SerializationOptions>, ...args: unknown[]): void {
    const formattedArgs = args.map(arg => smartSerialize(arg, options));
    logInternal(level, this.prefix, formattedArgs, true);
  }
}

/**
 * Default logger instance
 */
export const logger = new Logger();

/**
 * Create a logger with a specific prefix
 */
export function createLogger(prefix: string): Logger {
  return new Logger(prefix);
}

/**
 * Utility exports for direct use
 */
export const log = {
  debug: (...args: unknown[]) => logger.debug(...args),
  info: (...args: unknown[]) => logger.info(...args),
  warn: (...args: unknown[]) => logger.warn(...args),
  error: (...args: unknown[]) => logger.error(...args),
};
